import { Component, Input, OnInit } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { AttachmentTypeService } from '@services/attachment/attachment-type.service';
import { AttachmentUtilsService } from '@services/attachment/attachment-utils.service';
import { AttachmentViewModel } from 'src/app/business/attachment/view-models/attachment-view-model';
import { AttachmentDB } from 'src/app/common/repository/databases';
import { AttachmentDto } from 'src/app/shared/models/attachment/attachment-dto.model';
import { SelectionItem } from 'src/app/shared/models/selectionItem';
import { PreviewModalService } from 'src/app/shared/services/preview-modal.service';
import { QueryService } from 'src/app/shared/services/query/query.service';

@Component({
  selector: 'itl-prescription-request-attachments',
  templateUrl: './prescription-request-attachments.html',
  styleUrls: ['./prescription-request-attachments.scss'],
})
export class PrescriptionRequestAttachmentsComponent implements OnInit {
  @Input() attachmentTypes: number[] = [];
  @Input() patientId: string;
  public attachmentTypeSelectionItems: SelectionItem[] = [];
  public attachments: AttachmentViewModel[] = [];
  public selectedType: number;
  public loading = false;

  private _displayedType: number;

  public get allAttachmentsSelected(): boolean {
    if (this.attachments?.length) {
      return this.attachments.every(attachment => !!attachment.selected);
    }
  }

  constructor(
    private _translateService: TranslateService,
    private _queryService: QueryService,
    private _attachmentUtilsService: AttachmentUtilsService,
    private _modalController: ModalController,
    private _previewService: PreviewModalService,
    private _alertController: AlertController,
    private _attachmentTypeService: AttachmentTypeService
  ) {}

  async ngOnInit() {
    const selectionItems = await this._attachmentTypeService.getSelectionItems({
      sortAlphabetic: true,
    });
    this.attachmentTypeSelectionItems = selectionItems.filter(type =>
      this.attachmentTypes.some(attachmentType => attachmentType === type.id)
    );
  }

  public async cancel() {
    await this._modalController.dismiss();
  }
  public async done() {
    await this._modalController.dismiss(this.attachments.filter(attachment => attachment.selected));
  }

  public async attachmentTypeSelected() {
    if (this._displayedType === this.selectedType) {
      return;
    }
    if (this.attachments == null) {
      this.attachments = [];
    } else if (this.attachments.some(attachement => attachement.selected)) {
      const abort = await this.askUserToDiscardSelection();
      if (abort) {
        this.selectedType = this._displayedType;
        return;
      }
    }

    this.loading = true;
    this._displayedType = this.selectedType;
    const unsortedAttachments = await this._queryService.search<AttachmentViewModel>(
      {
        query: `metadata.patientId:${this.patientId} metadata.type:${this.selectedType}`,
      },
      AttachmentDB
    );
    this.attachments = unsortedAttachments.sort((a: AttachmentDto, b: AttachmentDto) =>
      a?.metadata?.createdAt && b?.metadata?.createdAt
        ? new Date(b.metadata.createdAt).getTime() - new Date(a.metadata.createdAt).getTime()
        : 1
    );

    this.loading = false;
  }

  public hasTherapy(attachment: any): boolean {
    return attachment?.metadata?.therapyId > 0;
  }

  public getTherapyName(attachment: any): string {
    return this._attachmentUtilsService.getTherapyName(attachment);
  }

  public selectAllAttachments(allSelected: boolean) {
    if (allSelected === true) {
      this.attachments.forEach(attachment => {
        attachment.selected = false;
      });
    } else {
      this.attachments.forEach(attachment => {
        attachment.selected = true;
      });
    }
  }

  public async previewAttachment(attachment: AttachmentViewModel) {
    await this._previewService.previewAttachment(attachment);
  }

  private async askUserToDiscardSelection(): Promise<boolean> {
    return new Promise(async resolve => {
      const alert = await this._alertController.create({
        cssClass: 'overridePrimaryToVariant',
        header: this._translateService.instant('attachment.prescriptionRequest.discardSelectionHeader'),
        message: this._translateService.instant('attachment.prescriptionRequest.discardSelectionText'),
        buttons: [
          {
            text: this._translateService.instant('attachment.prescriptionRequest.proceedDiscardingSelection'),
            handler: () => {
              resolve(false);
            },
          },
          {
            text: this._translateService.instant('CANCEL_BUTTON'),
            handler: () => {
              resolve(true);
            },
          },
        ],
      });
      alert.present();
    });
  }
}
